import React, { useEffect, useRef, useState, useCallback } from "react";
import { useTranslation } from "react-i18next";
import { Play, Pause, Square, Loader2, Headphones, Rewind, FastForward, ChevronDown } from "lucide-react";
import { fetchNarration, SPEED_OPTIONS } from "../lib/voice";
import { usePreferences } from "../context/PreferencesContext";
import { useAudio } from "../context/AudioContext";
import { captureAudioEvent } from "../lib/analyticsEvents";

// Article-length audio player. Deliberately separate from the compact
// `ListenButton` used on landing/overview pages so this richer set of
// controls only appears on substantial editorial pages.
//
// Reuses the site's existing narration backend (`/api/voice/tts` →
// ElevenLabs `eleven_multilingual_v2`) and voice preferences. Narration
// language follows the active i18n locale, so a Portuguese page is read in
// Portuguese and Spanish in Spanish.
//
// Position preservation is native — playback resumes from `currentTime` on
// pause/resume, and rewind/forward operate on the same underlying `<audio>`
// element, so no narration is regenerated for scrubbing.

const SKIP_SECONDS = 15;

const formatTime = (s) => {
  if (!Number.isFinite(s) || s < 0) s = 0;
  const total = Math.floor(s);
  const mm = Math.floor(total / 60);
  const ss = total % 60;
  return `${mm}:${ss.toString().padStart(2, "0")}`;
};

export const ArticleAudioPlayer = ({
  text,
  pageType,       // "founder_feature" | "journal_article" | "foundation" | "perspective"
  pageSlug,       // for analytics
  readingMinutes, // for analytics
  preGeneratedAudio, // optional: { male?: string, female?: string } — self-hosted MP3 URLs
  testId = "article-audio-player",
}) => {
  const { t, i18n } = useTranslation();
  const narrationLang = ((i18n.language || "en").split("-")[0]);
  const { prefs, setPref } = usePreferences();
  const { duckAmbient, unduckAmbient } = useAudio() || {};

  const audioRef = useRef(null);
  const objectUrlRef = useRef(null);
  const [loading, setLoading] = useState(false);
  const [playing, setPlaying] = useState(false);
  const [ready, setReady] = useState(false);
  const [error, setError] = useState(null);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [speedMenuOpen, setSpeedMenuOpen] = useState(false);
  const progressFiredRef = useRef(new Set()); // Set<"25"|"50"|"75"|"100">

  // Fetch the narration audio once per article, on first play. Uses one
  // request for the whole article so total duration is known immediately and
  // scrubbing works natively.
  //
  // If a self-hosted pre-generated MP3 is provided for the active voice, use
  // it directly — no /api/voice/tts round-trip, no ElevenLabs latency, and
  // playback can begin as soon as the browser starts streaming the file.
  const ensureAudio = useCallback(async () => {
    if (objectUrlRef.current) return true;
    setLoading(true);
    setError(null);
    try {
      const preUrl = preGeneratedAudio && preGeneratedAudio[prefs.voice];
      if (preUrl) {
        objectUrlRef.current = preUrl;
        if (audioRef.current) {
          audioRef.current.src = preUrl;
          audioRef.current.playbackRate = prefs.speed || 1;
        }
        return true;
      }
      const url = await fetchNarration(text, prefs.voice, narrationLang);
      if (!url) throw new Error("no url");
      objectUrlRef.current = url;
      if (audioRef.current) {
        audioRef.current.src = url;
        audioRef.current.playbackRate = prefs.speed || 1;
      }
      return true;
    } catch (e) {
      setError(e && e.message ? e.message : "audio_unavailable");
      return false;
    } finally {
      setLoading(false);
    }
  }, [text, prefs.voice, narrationLang, prefs.speed, preGeneratedAudio]);

  // Release the blob URL when the component unmounts. Pre-generated
  // (self-hosted) URLs are static file paths — revoking them is both
  // unnecessary and harmless-but-noisy, so we skip that branch.
  useEffect(() => () => {
    const u = objectUrlRef.current;
    if (u && u.startsWith("blob:")) {
      try { URL.revokeObjectURL(u); } catch {}
    }
    objectUrlRef.current = null;
  }, []);

  // Attach media listeners on mount.
  useEffect(() => {
    const a = audioRef.current;
    if (!a) return;
    const onLoaded = () => { setDuration(a.duration || 0); setReady(true); };
    const onTime = () => {
      const ct = a.currentTime || 0;
      setCurrentTime(ct);
      const d = a.duration || 0;
      if (d > 0) {
        const pct = (ct / d) * 100;
        const fire = (mark) => {
          if (!progressFiredRef.current.has(mark)) {
            progressFiredRef.current.add(mark);
            captureAudioEvent(`audio_progress_${mark}`, analyticsProps());
          }
        };
        if (pct >= 25) fire("25");
        if (pct >= 50) fire("50");
        if (pct >= 75) fire("75");
      }
    };
    const onPlay = () => { setPlaying(true); captureAudioEvent("audio_started", analyticsProps()); duckAmbient && duckAmbient(); };
    const onPause = () => { setPlaying(false); if (!a.ended) captureAudioEvent("audio_paused", analyticsProps()); unduckAmbient && unduckAmbient(); };
    const onEnded = () => {
      setPlaying(false);
      captureAudioEvent("audio_completed", analyticsProps());
      unduckAmbient && unduckAmbient();
    };
    a.addEventListener("loadedmetadata", onLoaded);
    a.addEventListener("timeupdate", onTime);
    a.addEventListener("play", onPlay);
    a.addEventListener("pause", onPause);
    a.addEventListener("ended", onEnded);
    return () => {
      a.removeEventListener("loadedmetadata", onLoaded);
      a.removeEventListener("timeupdate", onTime);
      a.removeEventListener("play", onPlay);
      a.removeEventListener("pause", onPause);
      a.removeEventListener("ended", onEnded);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [duckAmbient, unduckAmbient]);

  // Release the blob URL when the component unmounts.
  useEffect(() => () => {
    if (objectUrlRef.current) {
      try { URL.revokeObjectURL(objectUrlRef.current); } catch {}
      objectUrlRef.current = null;
    }
  }, []);

  const analyticsProps = useCallback(() => ({
    page_type: pageType || null,
    page_slug: pageSlug || null,
    content_language: narrationLang,
    selected_voice: prefs.voice || null,
    selected_playback_speed: prefs.speed || 1,
    estimated_reading_minutes: readingMinutes || null,
    audio_player_variant: "full",
  }), [pageType, pageSlug, narrationLang, prefs.voice, prefs.speed, readingMinutes]);

  const togglePlay = async () => {
    const a = audioRef.current;
    if (!a) return;
    if (!objectUrlRef.current) {
      const ok = await ensureAudio();
      if (!ok) return;
    }
    if (a.paused) {
      try { await a.play(); } catch (e) { setError("playback_blocked"); }
    } else {
      a.pause();
    }
  };

  const stop = () => {
    const a = audioRef.current;
    if (!a) return;
    a.pause();
    a.currentTime = 0;
    progressFiredRef.current.clear();
  };

  const skip = (delta) => {
    const a = audioRef.current;
    if (!a || !Number.isFinite(a.currentTime)) return;
    const next = Math.max(0, Math.min((a.duration || 0), (a.currentTime || 0) + delta));
    a.currentTime = next;
    setCurrentTime(next);
  };

  const onScrub = (e) => {
    const a = audioRef.current;
    if (!a || !Number.isFinite(a.duration) || a.duration <= 0) return;
    const value = Number(e.target.value);
    a.currentTime = value;
    setCurrentTime(value);
  };

  const setSpeed = (v) => {
    setPref("speed", v);
    if (audioRef.current) audioRef.current.playbackRate = v;
    setSpeedMenuOpen(false);
    captureAudioEvent("audio_speed_changed", { ...analyticsProps(), selected_playback_speed: v });
  };

  const listenLabel = t("listen.toArticle", { defaultValue: "Listen to this article" });
  const canPlay = !loading && !error;
  const progress = duration > 0 ? (currentTime / duration) * 100 : 0;

  return (
    <div
      data-testid={testId}
      role="region"
      aria-label={listenLabel}
      className="inline-flex flex-col gap-3 border border-subtle-custom bg-surface-custom/60 px-4 py-3 md:px-5 md:py-4 rounded-none max-w-full"
    >
      <audio ref={audioRef} preload="none" data-testid={`${testId}-audio`} />

      {/* Controls row */}
      <div className="flex items-center gap-2 md:gap-3 flex-wrap">
        <button
          type="button"
          onClick={togglePlay}
          disabled={loading}
          data-testid={`${testId}-play`}
          aria-label={playing ? t("listen.pause", { defaultValue: "Pause" }) : listenLabel}
          aria-pressed={playing}
          className="inline-flex items-center gap-2 px-3 py-1.5 border border-subtle-custom hover:border-[var(--accent-bronze)] text-[11px] font-sans uppercase tracking-[0.2em] text-primary-custom transition-colors"
        >
          {loading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : playing ? <Pause className="w-3.5 h-3.5" /> : <Headphones className="w-3.5 h-3.5" />}
          <span>{playing ? t("listen.pause", { defaultValue: "Pause" }) : listenLabel}</span>
        </button>

        <button
          type="button"
          onClick={() => skip(-SKIP_SECONDS)}
          disabled={!ready || !canPlay}
          data-testid={`${testId}-rewind`}
          aria-label={t("listen.rewind15", { defaultValue: "Rewind 15 seconds" })}
          className="inline-flex items-center gap-1 px-2 py-1.5 border border-subtle-custom hover:border-[var(--accent-bronze)] text-[10px] font-sans uppercase tracking-[0.15em] text-secondary-custom hover:text-primary-custom transition-colors disabled:opacity-40"
        >
          <Rewind className="w-3 h-3" />
          <span aria-hidden="true">15</span>
        </button>

        <button
          type="button"
          onClick={() => skip(SKIP_SECONDS)}
          disabled={!ready || !canPlay}
          data-testid={`${testId}-forward`}
          aria-label={t("listen.forward15", { defaultValue: "Forward 15 seconds" })}
          className="inline-flex items-center gap-1 px-2 py-1.5 border border-subtle-custom hover:border-[var(--accent-bronze)] text-[10px] font-sans uppercase tracking-[0.15em] text-secondary-custom hover:text-primary-custom transition-colors disabled:opacity-40"
        >
          <span aria-hidden="true">15</span>
          <FastForward className="w-3 h-3" />
        </button>

        <button
          type="button"
          onClick={stop}
          disabled={!ready}
          data-testid={`${testId}-stop`}
          aria-label={t("listen.stop", { defaultValue: "Stop" })}
          className="inline-flex items-center px-2 py-1.5 border border-subtle-custom hover:border-[var(--accent-bronze)] text-secondary-custom hover:text-primary-custom transition-colors disabled:opacity-40"
        >
          <Square className="w-3 h-3" />
        </button>

        {/* Speed selector — visible dropdown */}
        <div className="relative">
          <button
            type="button"
            onClick={() => setSpeedMenuOpen((v) => !v)}
            data-testid={`${testId}-speed`}
            aria-haspopup="listbox"
            aria-expanded={speedMenuOpen}
            aria-label={t("listen.speedLabel", { defaultValue: "Playback speed" })}
            className="inline-flex items-center gap-1 px-2 py-1.5 border border-subtle-custom hover:border-[var(--accent-bronze)] text-[10px] font-sans uppercase tracking-[0.15em] text-secondary-custom hover:text-primary-custom transition-colors"
          >
            <span>{(prefs.speed || 1)}×</span>
            <ChevronDown className="w-3 h-3" />
          </button>
          {speedMenuOpen && (
            <ul
              role="listbox"
              aria-label={t("listen.speedLabel", { defaultValue: "Playback speed" })}
              data-testid={`${testId}-speed-menu`}
              className="absolute z-50 mt-1 right-0 min-w-[80px] border border-subtle-custom bg-surface-custom shadow-xl"
            >
              {SPEED_OPTIONS.map((s) => (
                <li key={s}>
                  <button
                    type="button"
                    role="option"
                    aria-selected={prefs.speed === s}
                    onClick={() => setSpeed(s)}
                    className={`w-full text-left px-3 py-1.5 text-[10px] font-sans uppercase tracking-[0.15em] hover:bg-[var(--accent-bronze)]/10 ${prefs.speed === s ? "text-[var(--accent-bronze)]" : "text-secondary-custom"}`}
                  >
                    {s}×
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>

        {/* Elapsed / total */}
        <span
          data-testid={`${testId}-time`}
          className="ml-auto text-[10px] font-sans tabular-nums text-secondary-custom tracking-[0.05em]"
          aria-live="off"
        >
          <span aria-label={t("listen.elapsed", { defaultValue: "Elapsed" })}>{formatTime(currentTime)}</span>
          <span aria-hidden="true"> / </span>
          <span aria-label={t("listen.total", { defaultValue: "Total" })}>{formatTime(duration)}</span>
        </span>
      </div>

      {/* Progress / scrub bar */}
      <input
        type="range"
        min={0}
        max={Math.max(duration || 0, 0.001)}
        step={0.1}
        value={currentTime}
        onChange={onScrub}
        disabled={!ready}
        data-testid={`${testId}-scrub`}
        aria-label={t("listen.progress", { defaultValue: "Playback progress" })}
        className="w-full accent-[var(--accent-bronze)] cursor-pointer disabled:opacity-40"
        style={{
          // Visual bar for browsers that don't style range natively
          background: `linear-gradient(to right, var(--accent-bronze) ${progress}%, var(--subtle-custom) ${progress}%)`,
          height: "4px",
        }}
      />

      {error && (
        <p role="alert" className="text-[10px] font-sans uppercase tracking-[0.15em] text-secondary-custom/80">
          {t("listen.error", { defaultValue: "Audio is unavailable right now." })}
        </p>
      )}
    </div>
  );
};

export default ArticleAudioPlayer;
